import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Owner from '@ember/owner';
import type { LightboxState } from '../types/lightbox';

interface HistoryState {
  lightbox?: boolean;
}

/**
 * Lightbox Service
 *
 * Manages fullscreen image viewing with zoom/pan support and browser
 * history integration for back button handling.
 */
export default class Lightbox extends Service {
  /** Current lightbox state (null when closed) */
  @tracked state: LightboxState | null = null;

  constructor(owner: Owner) {
    super(owner);
    this.setupPopstateListener();
  }

  willDestroy(): void {
    super.willDestroy();
    window.removeEventListener('popstate', this.handlePopstate);
  }

  /**
   * Open the lightbox with an image
   */
  open(imageUrl: string, altText: string): void {
    // Don't reopen if already showing the same image
    if (this.state?.imageUrl === imageUrl) {
      return;
    }

    this.state = {
      imageUrl,
      altText,
      openedAt: Date.now(),
    };

    // Add history entry for back button support
    const state: HistoryState = { lightbox: true };
    history.pushState(state, '');
  }

  /**
   * Close the lightbox
   * Arrow function to preserve `this` when passed to event handlers
   */
  close = (): void => {
    if (!this.state) {
      return;
    }

    this.state = null;

    // Go back in history to remove our pushed state
    history.back();
  };

  private setupPopstateListener(): void {
    window.addEventListener('popstate', this.handlePopstate);
  }

  private handlePopstate = (event: PopStateEvent): void => {
    const state = event.state as HistoryState | null;

    // If we're navigating away from a lightbox state, close it
    if (!state?.lightbox && this.state) {
      this.state = null;
    }
  };
}

declare module '@ember/service' {
  interface Registry {
    lightbox: Lightbox;
  }
}
